import { memo, useCallback, useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { useNavigate } from "../../hooks/useNavigate";
import { useWindowResize } from "../../hooks/useWindowResize";
import { useSlideUpAnimation } from "../../hooks/animationHooks/useSlideUpAnimation";
import { MixpanelContext } from "../../context/AnalyticsService";
import { getAccountState, LoginState } from "../../redux/slices/accountSlice";
import { DataLoaders } from "../../redux/slices/dataSlice";
import LandingPagesWrapper from "../../components/LandingPage/LandingPagesWrapper";
import SubscriptionPlans from "../../components/Subscription/SubscriptionPlans";
import TextingThatFeelsHumanFold from "../../components/LandingPage/TextingThatFeelsHumanFold";
import PlanComparisonTable from "../../components/LandingPage/PlanComparisonTable";
import RectangleButton from "../../components/Buttons/RectangleButton";
import LabelButton from "../../components/Buttons/LabelButton";
import ShareMarkitNumber from "../../assets/LandingPage/ShareMarkitNumber.png";
import { NavigationId } from "../../navigation/AppParamList";
import { LoginStage } from "../Login/LoginWelcome";
import { Colors } from "../../utils/colors";
import { PlanTypeV4 } from "@markit/common.types";
import { MARKIT_TEXTING_CREDITS, MARKITAI } from "@markit/common.utils";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";

const PricingPage = memo(function PricingPageFn() {
  const { account } = useSelector(getAccountState);
  const { accountData, loggedIn } = account;
  const mixpanel = useContext(MixpanelContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedCardOption, setSelectedCardOption] = useState(2);

  const windowWidth = useWindowResize();

  useSlideUpAnimation();

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth, true),
    [windowWidth]
  );

  const styles = {
    button: {
      padding: "12px 16px",
      borderRadius: 10,
      width: "fit-content",
    },

    headerLabel: {
      fontSize: isDesktopSize ? 16 : 14,
      fontWeight: 500,
    },

    foldContainer: {
      paddingBlock: isDesktopSize ? 80 : 60,
      backgroundImage: "linear-gradient(to bottom, #141516 0%, #000000 31%)",
    },

    mainTitle: {
      fontSize: isDesktopSize ? 80 : 60,
      fontWeight: 500,
      letterSpacing: "-1px",
      backgroundImage: "linear-gradient(to bottom, #FFFFFF 40%, #787878 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
    },

    headerSubtext: {
      fontSize: 18,
      fontWeight: 500,
      color: Colors.GRAY1,
      letterSpacing: "-0.5px",
      lineHeight: "24px",
    },

    headerText: {
      fontSize: isDesktopSize ? 64 : 48,
      fontWeight: 500,
      letterSpacing: "-0.5px",
      color: Colors.WHITE,
    },

    largeImage: {
      width: "100%",
      height: "auto",
    },

    safeView: {
      paddingInline: 20,
    },
  };

  const renderFoldHeaderLabel = useCallback(
    (label: string, iconName: string, color: string) => (
      <div
        className="AlignedRow"
        style={{ gap: 10, paddingBottom: isDesktopSize ? 10 : 7 }}
      >
        <Icon icon={iconName} height={14} style={{ color }} />
        <span style={{ ...styles.headerLabel, color }}>{label}</span>
      </div>
    ),
    [styles.headerLabel, isDesktopSize]
  );

  const signUpForPlanOnPress = useCallback(
    (cardOption: number) => {
      if (mixpanel) {
        mixpanel.track("Website: Pricing Pressed Subscription Plan", {
          distinct_id: accountData.uid,
          is_logged_in: loggedIn === LoginState.LOGGED_IN,
          plan:
            cardOption === 0
              ? PlanTypeV4.FREE.name
              : cardOption === 1
                ? PlanTypeV4.ESSENTIAL.name
                : cardOption === 2
                  ? PlanTypeV4.PRO.name
                  : PlanTypeV4.BUSINESS.name,
        });
      }
      if (loggedIn === LoginState.LOGGED_IN && accountData.inCreatorMode) {
        navigate("/home");
      } else {
        navigate(NavigationId.SIGN_IN, {
          state: { preLoginStage: LoginStage.EMAIL_SIGNUP },
        });
      }
    },
    [accountData.inCreatorMode, accountData.uid, loggedIn, mixpanel, navigate]
  );

  const getStartedOnPress = useCallback(
    (btnSource: string) => {
      if (mixpanel) {
        mixpanel.track("Website: Home Pressed Get Started", {
          distinct_id: accountData.uid,
          is_logged_in: loggedIn === LoginState.LOGGED_IN,
          btn_source: btnSource,
        });
      }
      if (loggedIn === LoginState.LOGGED_IN) {
        dispatch(DataLoaders.user(accountData.uid));
        navigate(`/u/${accountData.username}`, {
          state: { username: accountData.username },
        });
      } else {
        navigate(NavigationId.WELCOME);
      }
    },
    [
      accountData.uid,
      accountData.username,
      dispatch,
      loggedIn,
      navigate,
      mixpanel,
    ]
  );

  return (
    <LandingPagesWrapper
      helmetData={{
        title: "Pricing | Markit AI",
        metaContent:
          "Simple, transparent pricing to grow your text list. Get more texting, integrations, and premium features with Markit+.",
        link: MARKITAI + "/pricing",
      }}
      noRedirect
    >
      <div
        className="AlignedColumn"
        style={{
          gap: isDesktopSize ? 60 : 80,
          color: Colors.WHITE,
          paddingTop: 50,
          paddingBottom: 60,
        }}
      >
        <div
          className="AlignedColumn"
          style={{
            gap: isDesktopSize ? 24 : 21,
            width: isDesktopSize ? 926 : undefined,
            paddingInline: isDesktopSize ? 0 : 14,
          }}
        >
          <span style={styles.mainTitle}>Pricing</span>
          <span
            style={{
              fontSize: 18,
              color: Colors.GRAY1,
              textAlign: "center",
              width: isDesktopSize ? 722 : undefined,
              lineHeight: "24px",
            }}
          >
            Build, monetize, and scale{!isDesktopSize ? <br /> : " "} your
            audience with text.
            <br />
            Start free. Upgrade to Markit+ as you grow
          </span>
        </div>
        <div
          style={{
            paddingInline: 14, // Allow Subscription Plan cards to expand to full width so it doesn't snap to less plans per row as soon
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <SubscriptionPlans
            selectedCardOption={selectedCardOption}
            setSelectedCardOption={setSelectedCardOption}
            itemOnPress={signUpForPlanOnPress}
            homePage
            contentPaddingInline={isDesktopSize ? 180 : 14}
          />
        </div>
      </div>
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{ ...styles.foldContainer, ...styles.safeView }}
      >
        <div
          className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
          style={{
            gap: 24,
            textAlign: isDesktopSize ? "center" : "left",
            maxWidth: 1152,
            margin: "0 auto",
            width: "100%",
          }}
        >
          <LabelButton
            text="Compare plans"
            color={Colors.BLUE5}
            backgroundColor={Colors.BLUE5 + "1a"}
            icon={
              <Icon
                icon="ion:wallet"
                height={14}
                style={{ color: Colors.BLUE5 }}
              />
            }
            borderColor={Colors.BLUE5}
            containerStyles={{ fontSize: 16, fontWeight: 500 }}
          />
          <h2 style={styles.headerText}>Everything you need to grow</h2>
          <span style={styles.headerSubtext}>
            With Markit, you can sell products, memberships, and live events in
            one platform. Only pay more when your audience grows.
          </span>
        </div>
        <div
          className="slide-up"
          style={{
            paddingBlock: 60,
            width: "100%",
          }}
        >
          <PlanComparisonTable />
        </div>
      </div>
      <div style={styles.foldContainer}>
        <div style={{ maxWidth: 1152, margin: "0 auto", ...styles.safeView }}>
          <div
            className={isDesktopSize ? "RowNormal" : "ColumnNormal"}
            style={{ gap: isDesktopSize ? 149 : 60 }}
          >
            <div className="ColumnNormal">
              {renderFoldHeaderLabel("Pricing", "ion:wallet", Colors.PURPLE7)}
              <div className="ColumnNormal" style={{ gap: 30 }}>
                <div className="ColumnNormal" style={{ gap: 24 }}>
                  <h2 style={styles.headerText}>
                    How Texting
                    <br />
                    Works
                  </h2>
                  <p style={styles.headerSubtext}>
                    Plans includes a number of monthly text credits. If you
                    exceed your included credits, additional texts are billed at
                    your plan’s rate.
                    <br />
                    <br />
                    You only pay more as your audience grows.
                  </p>
                </div>
                <div
                  className="AlignedRow"
                  style={{ gap: isDesktopSize ? 14 : 10 }}
                >
                  <RectangleButton
                    buttonLabel="What's a texting credit?"
                    onPress={() => window.open(MARKIT_TEXTING_CREDITS)}
                    altColor={Colors.WHITE}
                    altTextColor={Colors.BLACK}
                    iconRight={
                      <Icon
                        icon="ion:chevron-forward"
                        height={16}
                        style={{ color: Colors.BLACK }}
                      />
                    }
                    containerStyles={styles.button}
                  />
                </div>
              </div>
            </div>
            <img
              src={ShareMarkitNumber}
              alt="Share Markit Number"
              className="slide-up"
              style={{
                ...styles.largeImage,
                objectFit: "contain",
                maxWidth: 390,
              }}
            />
          </div>
        </div>
      </div>
      <TextingThatFeelsHumanFold
        isDesktopSize={isDesktopSize}
        getStartedOnPress={getStartedOnPress}
      />
    </LandingPagesWrapper>
  );
});

export default PricingPage;
